/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import * as d3 from 'd3';
import type {
  SoundingDiagramType,
  SoundingFeature,
} from '../../api/edr-api/types';
import type { Margin } from '../Soundings/drawUtils';
import {
  filterDomainRangeForParameter,
  SOUNDING_COLORS,
} from '../Soundings/drawUtils';
import type { ScalesType } from '../Soundings/types';
import { pressureFromPosition } from '../StaticLines/bijvoetUtils';

// Check if the value is outside the domain, if so, return NaN
// Otherwise, return the interpolated value
const createClampedInterpolationFunc = (
  interpolationFunc: d3.ScaleLinear<number, number, never>,
  domain: number[],
): ((value: number) => number) => {
  return (value: number): number => {
    if (value < domain[domain.length - 1] || value > domain[0]) {
      return NaN;
    }

    return interpolationFunc(value);
  };
};

interface DrawSoundingsTooltipData {
  pressureData: number[];
  interpolatePressure: (value: number) => number;
  interpolateTemperature: (value: number) => number;
  interpolateDewpoint: (value: number) => number;
  interpolateWindSpeed: (value: number) => number;
  interpolateWindDirection: (value: number) => number;
  tooltipColor: string;
}

// Construct interpolation functions to use when hovering between data points
// Ensure NaN/null values are removed and cut the domain to the upper and lower pressure bounds of the parameter
export const constructTooltipData = (
  data: SoundingFeature[][],
): DrawSoundingsTooltipData[] => {
  const tooltipData = data.map((features, index) => {
    const color = SOUNDING_COLORS[index] || SOUNDING_COLORS[0];
    const pressureData = features.map((feature) => feature.properties.pressure);
    const tempData = features.map((feature) => feature.properties.temp);
    const dewpointData = features.map((feature) => feature.properties.dewpoint);
    const windDirData = features.map(
      (feature) => feature.properties.windDirection,
    );
    const windSpeedData = features.map(
      (feature) => feature.properties.windSpeed,
    );

    const interpolatePressure = d3
      .scaleLinear()
      .domain(pressureData)
      .range(pressureData)
      .clamp(true);

    const [tempPressure, filteredTempData] = filterDomainRangeForParameter(
      pressureData,
      tempData,
    );
    const interpolateTemperature = d3
      .scaleLinear()
      .domain(tempPressure)
      .range(filteredTempData)
      .clamp(true);

    const [dewpointPressure, filteredDewpointData] =
      filterDomainRangeForParameter(pressureData, dewpointData);
    const interpolateDewpoint = d3
      .scaleLinear()
      .domain(dewpointPressure)
      .range(filteredDewpointData)
      .clamp(true);

    const [windSpeedPressure, filteredWindSpeedData] =
      filterDomainRangeForParameter(pressureData, windSpeedData);
    const interpolateWindSpeed = d3
      .scaleLinear()
      .domain(windSpeedPressure)
      .range(filteredWindSpeedData)
      .clamp(true);
    const [windDirPressure, filteredWindDirData] =
      filterDomainRangeForParameter(pressureData, windDirData);
    const interpolateWindDirection = d3
      .scaleLinear()
      .domain(windDirPressure)
      .range(filteredWindDirData)
      .clamp(true);

    return {
      pressureData,
      interpolatePressure: createClampedInterpolationFunc(
        interpolatePressure,
        pressureData,
      ),
      interpolateTemperature: createClampedInterpolationFunc(
        interpolateTemperature,
        tempPressure,
      ),
      interpolateDewpoint: createClampedInterpolationFunc(
        interpolateDewpoint,
        dewpointPressure,
      ),
      interpolateWindSpeed: createClampedInterpolationFunc(
        interpolateWindSpeed,
        windSpeedPressure,
      ),
      interpolateWindDirection: createClampedInterpolationFunc(
        interpolateWindDirection,
        windDirPressure,
      ),
      tooltipColor: color,
    };
  });
  return tooltipData.filter((data) => data.pressureData.length > 0);
};

interface DrawSoundingsTooltipProps {
  event: { pageY: number; clientY: number };
  yScale: d3.ScaleLinear<number, number>;
  svgRectangle: DOMRect;
  margin: Margin;
  width: number;
  pressureRefd3?: d3.Selection<HTMLDivElement | null, unknown, null, undefined>;
  tempRefd3?: d3.Selection<HTMLDivElement | null, unknown, null, undefined>;
  dewpointRefd3?: d3.Selection<HTMLDivElement | null, unknown, null, undefined>;
  windRefd3?: d3.Selection<HTMLDivElement | null, unknown, null, undefined>;
  tooltipData: DrawSoundingsTooltipData[];
  diagramType: SoundingDiagramType;
}

const getParamHtml = (
  paramValue: number,
  colorIndex: number,
  unit: string,
  isLastElement: boolean,
  color: string,
  noDistance = false,
): string => {
  if (colorIndex === 0) {
    return `<p style="${noDistance || isLastElement ? 'margin: 0;' : 'margin-top: 0;'} color: ${color}">${!isNaN(paramValue) && paramValue !== null ? `${paramValue.toFixed(1)}${unit}` : '-'}${noDistance ? '' : '<br/>'}</p>`;
  }
  if (isLastElement) {
    return `<p style="${noDistance ? 'margin: 0;' : 'margin-bottom: 0;'}  color: ${color}">${!isNaN(paramValue) && paramValue !== null ? `${paramValue.toFixed(1)}${unit}` : '-'}</p>`;
  }
  return `<p style="${noDistance ? 'margin: 0;' : 'margin-top: 0;'} color: ${color}">${!isNaN(paramValue) && paramValue !== null ? `${paramValue.toFixed(1)}${unit}` : '-'}${noDistance ? '' : '<br/>'}</p>`;
};

const extractPressureFromY = (
  diagramType: SoundingDiagramType,
  y: number,
  yScale: ScalesType,
): number => {
  if (diagramType === 'BIJVOET') {
    const frac = yScale.invert(y);
    return pressureFromPosition(frac);
  }
  return yScale.invert(y);
};

export const drawSoundingsTooltip = ({
  event,
  yScale,
  svgRectangle,
  pressureRefd3,
  tempRefd3,
  dewpointRefd3,
  windRefd3,
  margin,
  width,
  tooltipData,
  diagramType,
}: DrawSoundingsTooltipProps): void => {
  if (tooltipData.length === 0) {
    return;
  }

  const [, y] = d3.pointer(event);
  const pressureY = event.clientY - svgRectangle.top - 10;
  const pressure = extractPressureFromY(diagramType, y, yScale);

  const interpolatedData = tooltipData.map((dataSet) => {
    return {
      hoveredPressure: dataSet.interpolatePressure(pressure),
      hoveredTemp: dataSet.interpolateTemperature(pressure),
      hoveredDewpoint: dataSet.interpolateDewpoint(pressure),
      hoveredWindDirection: dataSet.interpolateWindDirection(pressure),
      hoveredWindSpeed: dataSet.interpolateWindSpeed(pressure),
      color: dataSet.tooltipColor,
    };
  });

  // Pressure
  pressureRefd3
    ?.style('display', 'block')
    .style('left', `${margin.left + margin.right + 4}px`) // 4 to move it slightly away from y-axis
    .style('top', `${pressureY}px`)
    .html(`${Math.round(pressure)}hPa`);

  // Temperature
  tempRefd3
    ?.style('display', 'block')
    .style('left', `${0.75 * width + margin.left}px`)
    .style('top', `${pressureY}px`)
    .html(() =>
      interpolatedData
        .map((dataSet, index) => {
          return getParamHtml(
            dataSet.hoveredTemp,
            index,
            '°C',
            index === interpolatedData.length - 1,
            dataSet.color,
          );
        })
        .join(''),
    );

  // Dewpoint temperature
  dewpointRefd3
    ?.style('display', 'block')
    .style('left', `${width / 3 + margin.left}px`)
    .style('top', `${pressureY}px`)
    .html(
      interpolatedData
        .map((dataSet, index) => {
          return getParamHtml(
            dataSet.hoveredDewpoint,
            index,
            '°C',
            index === interpolatedData.length - 1,
            dataSet.color,
          );
        })
        .join(''),
    );

  // Wind
  windRefd3
    ?.style('display', 'block')
    .style('left', `${width + margin.left + margin.right}px`)
    .style('top', `${pressureY}px`)
    .html(() => {
      const windSpeedStrings = interpolatedData.map((dataSet, index) => {
        return getParamHtml(
          dataSet.hoveredWindSpeed,
          index,
          'kts',
          index === interpolatedData.length - 1,
          dataSet.color,
          true,
        );
      });
      const windDirStrings = interpolatedData.map((dataSet, index) => {
        return getParamHtml(
          dataSet.hoveredWindDirection,
          index,
          '°',
          index === interpolatedData.length - 1,
          dataSet.color,
          index === interpolatedData.length - 1,
        );
      });

      return windSpeedStrings
        .map((windSpeedString, index) => {
          return `${windSpeedString}${windDirStrings[index]}`;
        })
        .join('');
    });
};
